/**
 * Hotel stays, read off the visits.
 *
 * Each time at the hotel is its own activity — Arrived, maybe In Room, Left —
 * so leaving for the park and coming back is two visits, each with its own
 * times, and whatever happened at the hotel nests under the visit it happened
 * in. A *stay* is those visits taken together: a trip's visits to one hotel,
 * in the order they happened, until one of them logs Checked Out. The next
 * visit to that hotel after a check-out starts a new stay, and a split stay
 * (two hotels on one trip) is simply two stays.
 *
 * Nothing about a stay is stored: it's worked out from the visits each time,
 * like a travel day is from its legs, so trips logged before stays existed
 * group themselves and an edited visit can never leave a stay stale. The only
 * things a stay keeps of its own — the room number and room type — live on its
 * first visit (`STAY_ROOM_KEY`, `STAY_CONFIRMATION_KEY`).
 *
 * A stay never becomes a parent activity: an open stay would read as "where
 * you are" all week, filing park rides under the hotel, and it would cross the
 * trip's segments, which an activity tree can't. Hence visits plus a stay
 * worked out over them (lhotchkiss/TripTracker#138).
 */

import type { InteractionEvent } from "../types";
import { STAY_ACTIVE_WINDOW_MS, getActivityTimeRange, isActivityOpen } from "./tripDisplay";
import { addDaysToKey, dayKeyInZone, daysBetweenKeys } from "./calendarDay";

/** Where a stay's room number is kept: its first visit's `customResponses`. */
export const STAY_ROOM_KEY = "stay_room";
/** Where a stay's room type / view is kept ("Lagoon view, 2 queens"), beside the room. */
export const STAY_ROOM_TYPE_KEY = "stay_room_type";

export interface StayVisitLike {
  id: string;
  entityId?: string | null;
  /** A custom hotel's typed name (an activity with no place, typed HOTEL). */
  name?: string | null;
  events?: InteractionEvent[];
  customResponses?: Record<string, unknown> | null;
}

export interface HotelStay<T extends StayVisitLike = StayVisitLike> {
  /** The stay's first visit's id — the stay's address (`/trips/:id/stays/:stayId`). */
  id: string;
  /** Which hotel: the place's id, or `name:<typed name>` for a custom hotel. */
  key: string;
  /** The hotel place; null for a custom hotel, which only has a typed name. */
  entityId: string | null;
  /** A custom hotel's typed name, as on its first visit. */
  name: string | null;
  /** Oldest first. */
  visits: T[];
  /** Checked In, else the first visit's arrival. */
  checkIn: Date | null;
  /** Checked Out, when it's been logged. */
  checkOut: Date | null;
  /** First visit's first time. */
  start: Date | null;
  /** Check-out, else the last visit's last time. */
  end: Date | null;
  /** True once a visit logs Checked Out — nothing after it joins this stay. */
  checkedOut: boolean;
  /** Sum of the visits' lengths, open visits up to `now`. */
  timeAtHotelMs: number;
  room: string | null;
  roomType: string | null;
}

const normalize = (label: string | null | undefined) =>
  (label ?? "").toLowerCase().replace(/[^a-z]/g, "");

/** "Checked Out", "Check Out", "Check-out", "Checkout". */
export function isCheckOutEvent(ev: Pick<InteractionEvent, "customLabel">): boolean {
  const n = normalize(ev.customLabel);
  return n === "checkedout" || n === "checkout";
}

/** "Checked In" / "Check In" — not "Check In Line", which is the queue. */
export function isCheckInEvent(ev: Pick<InteractionEvent, "customLabel">): boolean {
  const n = normalize(ev.customLabel);
  return n === "checkedin" || n === "checkin";
}

function eventMs(ev: InteractionEvent): number | null {
  const ms = ev.eventTimestamp?.toDate?.()?.getTime();
  return typeof ms === "number" && Number.isFinite(ms) ? ms : null;
}

function firstEventMs(events: InteractionEvent[] | undefined, match: (ev: InteractionEvent) => boolean): number | null {
  let best: number | null = null;
  for (const ev of events ?? []) {
    if (!match(ev)) continue;
    const ms = eventMs(ev);
    if (ms != null && (best == null || ms < best)) best = ms;
  }
  return best;
}

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/**
 * Which hotel a visit is at, or null when it isn't a hotel visit: a HOTEL
 * place's id, or for a custom hotel — logged with no place, typed Hotel and
 * given a name — `name:` plus the name, so its visits group by what it's
 * called.
 */
export function hotelKeyOf(
  visit: Pick<StayVisitLike, "entityId" | "name" | "customResponses">,
  isHotel: (entityId: string) => boolean,
): string | null {
  if (visit.entityId) return isHotel(visit.entityId) ? visit.entityId : null;
  if (visit.customResponses?.entityType !== "HOTEL") return null;
  const name = text(visit.name);
  return name ? `name:${normalize(name)}` : null;
}

/** A stay's hotel place, looked up in `map`; undefined for a custom hotel. */
export function stayHotel<H>(stay: Pick<HotelStay, "entityId">, map: Record<string, H | undefined>): H | undefined {
  return stay.entityId ? map[stay.entityId] : undefined;
}

/** What to call a stay's hotel: the place's name, else the custom name. */
export function stayHotelName(
  stay: Pick<HotelStay, "name">,
  hotel: { displayName?: string | null } | undefined,
  fallback = "Hotel",
): string {
  return hotel?.displayName || stay.name || fallback;
}

/**
 * Group a trip's activities into hotel stays, oldest first.
 *
 * `isHotel` says which places are hotels (a HOTEL entity); a custom hotel
 * (no place, typed Hotel) counts by its name — see `hotelKeyOf`. `startOf` dates a
 * visit without logged times — a backfilled one — and defaults to its first
 * event; a visit with neither is left out, since it can't be put in order.
 */
export function groupHotelStays<T extends StayVisitLike>(
  activities: T[],
  isHotel: (entityId: string) => boolean,
  options: { startOf?: (visit: T) => Date | null; now?: Date } = {},
): HotelStay<T>[] {
  const startOf = options.startOf ?? ((v: T) => getActivityTimeRange(v.events).start);
  const nowMs = (options.now ?? new Date()).getTime();

  const visits = activities
    .map((visit) => ({ visit, key: hotelKeyOf(visit, isHotel), startMs: startOf(visit)?.getTime() ?? null }))
    .filter((x): x is { visit: T; key: string; startMs: number } => x.key != null && x.startMs != null)
    .sort((a, b) => a.startMs - b.startMs || a.visit.id.localeCompare(b.visit.id));

  const stays: HotelStay<T>[] = [];
  // The stay still taking visits, per hotel.
  const running = new Map<string, HotelStay<T>>();

  for (const { visit, key, startMs } of visits) {
    let stay = running.get(key);
    if (!stay) {
      stay = {
        id: visit.id,
        key,
        entityId: visit.entityId ?? null,
        name: visit.entityId ? null : text(visit.name),
        visits: [],
        checkIn: null,
        checkOut: null,
        start: new Date(startMs),
        end: null,
        checkedOut: false,
        timeAtHotelMs: 0,
        room: text(visit.customResponses?.[STAY_ROOM_KEY]),
        roomType: text(visit.customResponses?.[STAY_ROOM_TYPE_KEY]),
      };
      stays.push(stay);
      running.set(key, stay);
    }
    stay.visits.push(visit);

    const range = getActivityTimeRange(visit.events);
    const visitEndMs = range.end?.getTime() ?? range.start?.getTime() ?? startMs;
    const open = (visit.events?.length ?? 0) > 0 && isActivityOpen(visit.events);
    stay.timeAtHotelMs += Math.max(0, (open ? Math.max(nowMs, visitEndMs) : visitEndMs) - startMs);

    const checkInMs = firstEventMs(visit.events, isCheckInEvent);
    if (checkInMs != null && stay.checkIn == null) stay.checkIn = new Date(checkInMs);
    if (!stay.end || visitEndMs > stay.end.getTime()) stay.end = new Date(visitEndMs);

    const checkOutMs = firstEventMs(visit.events, isCheckOutEvent);
    if (checkOutMs != null) {
      stay.checkOut = new Date(checkOutMs);
      stay.checkedOut = true;
      running.delete(key);
    }
  }

  for (const stay of stays) {
    if (!stay.checkIn) stay.checkIn = stay.start;
  }
  return stays;
}

/**
 * Whether a stay is still going: no Checked Out, and either you're at the
 * hotel now or you left it recently. An old trip whose check-out was never
 * logged ends with its last visit.
 */
export function isStayRunning(stay: Pick<HotelStay, "checkedOut" | "end" | "visits">, now: Date = new Date()): boolean {
  if (stay.checkedOut) return false;
  const last = stay.visits[stay.visits.length - 1];
  if (last?.events?.length && isActivityOpen(last.events)) {
    return stay.end != null && now.getTime() - stay.end.getTime() < STAY_ACTIVE_WINDOW_MS;
  }
  return stay.end != null && now.getTime() - stay.end.getTime() <= BACK_AT_HOTEL_WINDOW_MS;
}

/** Until 6 AM, a running stay is still on the night before. */
const NIGHT_ROLLOVER_MS = 6 * 60 * 60 * 1000;

/**
 * Nights in a stay: calendar days from its first day to its last, read in
 * `zone`. A stay that's still running counts to `now`, so it reads as the
 * nights slept so far.
 */
export function stayNights(stay: HotelStay, zone?: string, now: Date = new Date()): number {
  if (!stay.start) return 0;
  // Running, the small hours still belong to last night: 3 AM after the
  // second evening there is night 2, not night 3.
  const last = isStayRunning(stay, now)
    ? new Date(Math.max(stay.start.getTime(), now.getTime() - NIGHT_ROLLOVER_MS))
    : stay.checkOut ?? stay.end ?? stay.start;
  const n = daysBetweenKeys(dayKeyInZone(stay.start, zone), dayKeyInZone(last, zone));
  return Math.max(0, n);
}

/** Which visit of its stay this is: `{ index: 2, count: 5 }` for "Visit 2 of 5". */
export function visitPositions(stays: HotelStay[]): Map<string, { index: number; count: number; stay: HotelStay }> {
  const positions = new Map<string, { index: number; count: number; stay: HotelStay }>();
  for (const stay of stays) {
    stay.visits.forEach((v, i) => positions.set(v.id, { index: i + 1, count: stay.visits.length, stay }));
  }
  return positions;
}

/** How long after leaving the hotel "Back at …" stays on offer. */
export const BACK_AT_HOTEL_WINDOW_MS = 36 * 60 * 60 * 1000;

/**
 * The stay you'd come back to right now, if any: the most recent stay, still
 * running (no Checked Out), with its last visit closed — you've left and not
 * yet logged coming back — within the last day and a half.
 *
 * Only the most recent stay counts, so once you've logged a second hotel (a
 * transfer day without a check-out logged at the first) the first one stops
 * being offered.
 */
export function stayToReturnTo<T extends StayVisitLike>(stays: HotelStay<T>[], now: Date = new Date()): HotelStay<T> | null {
  let latest: HotelStay<T> | null = null;
  let latestMs = -Infinity;
  for (const stay of stays) {
    const last = stay.visits[stay.visits.length - 1];
    const ms = getActivityTimeRange(last?.events).start?.getTime() ?? stay.start?.getTime() ?? -Infinity;
    if (ms > latestMs) {
      latest = stay;
      latestMs = ms;
    }
  }
  if (!latest || latest.checkedOut) return null;
  const last = latest.visits[latest.visits.length - 1];
  if (!last?.events?.length || isActivityOpen(last.events)) return null;
  const leftMs = latest.end?.getTime();
  if (leftMs == null || now.getTime() - leftMs > BACK_AT_HOTEL_WINDOW_MS || now.getTime() < leftMs) return null;
  return latest;
}

/** "Fri, Oct 24", on the trip's clock. */
export function formatStayDay(d: Date | null, zone?: string): string {
  if (!d) return "";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: zone });
}

/** "34h 43m" / "45m". */
export function formatStayDuration(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** "3 nights" / "1 night" / "Night 2" for a stay that's still running. */
export function stayNightsLabel(stay: HotelStay, zone?: string, now: Date = new Date()): string {
  const nights = stayNights(stay, zone, now);
  if (isStayRunning(stay, now)) return `Night ${nights + 1}`;
  return `${nights} ${nights === 1 ? "night" : "nights"}`;
}

/** Minutes since midnight of an instant, on `zone`'s clock. */
function minutesIntoDay(d: Date, zone?: string): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(d);
    const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
    const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
    return (h % 24) * 60 + m + d.getSeconds() / 60;
  } catch {
    return d.getHours() * 60 + d.getMinutes();
  }
}

export interface StayDayBar {
  /** "YYYY-MM-DD" on the trip's clock. */
  dayKey: string;
  /** The stretches at the hotel that day, as fractions of the day (0–1). */
  spans: { from: number; to: number }[];
}

/**
 * The stay day by day: for each calendar day it covers, when you were at the
 * hotel. Visits that run past midnight are split across the days they touch;
 * an open visit runs to `now`.
 */
export function stayDayBars(stay: HotelStay, zone?: string, now: Date = new Date()): StayDayBar[] {
  const byDay = new Map<string, { from: number; to: number }[]>();
  const DAY_MIN = 24 * 60;
  for (const visit of stay.visits) {
    const range = getActivityTimeRange(visit.events);
    if (!range.start) continue;
    const open = isActivityOpen(visit.events);
    let endMs = range.end?.getTime() ?? range.start.getTime();
    if (open) endMs = Math.max(endMs, Math.min(now.getTime(), range.start.getTime() + STAY_ACTIVE_WINDOW_MS));
    let cur = range.start.getTime();
    // Guard: one bar a day at most for two weeks.
    for (let guard = 0; cur < endMs && guard < 16; guard++) {
      const at = new Date(cur);
      const key = dayKeyInZone(at, zone);
      const startMin = minutesIntoDay(at, zone);
      const toMidnightMs = (DAY_MIN - startMin) * 60000;
      const segEnd = Math.min(endMs, cur + toMidnightMs);
      const endMin = startMin + (segEnd - cur) / 60000;
      const spans = byDay.get(key) ?? [];
      spans.push({ from: startMin / DAY_MIN, to: Math.min(1, endMin / DAY_MIN) });
      byDay.set(key, spans);
      cur = segEnd;
    }
  }
  // Every day from the first to the last, including any spent wholly away.
  const keys = [...byDay.keys()].sort();
  const firstKey = keys[0];
  const lastKey = keys[keys.length - 1];
  if (!firstKey || !lastKey) return [];
  const bars: StayDayBar[] = [];
  for (let k = firstKey, i = 0; k <= lastKey && i < 60; i++, k = addDaysToKey(k, 1)) {
    bars.push({ dayKey: k, spans: byDay.get(k) ?? [] });
  }
  return bars;
}
